import { isEmpty } from "lodash";
import { getNetTotalEnergyConsumption } from "utils/results";

const tierThresholds = (totalHouseVolume) =>
    ({
        above300m3: {
            overall: { 1: 0, 2: 10, 3: 20, 4: 40, 5: 70 },
            heatLoss: { 1: -1, 2: 5, 3: 10, 4: 20, 5: 40 },
        },
        belowEqual300m3: {
            overall: { 1: 0, 2: 0, 3: 10, 4: 30, 5: 60 },
            heatLoss: { 1: -1, 2: 0, 3: 5, 4: 15, 5: 25 },
        },
    })[totalHouseVolume > 300 ? "above300m3" : "belowEqual300m3"];

const euiGrossHeatLossTargetTier = (budget) => [
    { target: 1.0 * budget, tier: 1 },
    { target: 0.95 * budget, tier: 2 },
    { target: 0.9 * budget, tier: 3 },
    { target: 0.8 * budget, tier: 4 },
    { target: 0.6 * budget, tier: 5 },
];

const euiEnergyConsumptionTargetTier = (budget) => [
    { target: 1.0 * budget, tier: 1 },
    { target: 0.9 * budget, tier: 2 },
    { target: 0.8 * budget, tier: 3 },
    { target: 0.6 * budget, tier: 4 },
    { target: 0.3 * budget, tier: 5 },
];

const determineEuiCompliance = (value, tiers) => {
    // Check the given value against the tier targets
    let complianceTier = 0;
    for (const tier of tiers) {
        if (value <= tier.target) {
            complianceTier = tier.tier;
        }
    }

    // If the number does not meet any tier lowest tier
    return complianceTier;
};

export const getNbc2020Tier = (propResults = {}, refResults = {}, totalHouseVolume = 500) => {
    const {
        Annual: { HeatLoss: { _attributes: { total: propGrossHeatLoss } = {} } = {} } = {},
        Other: { _attributes: { designCoolLossRate: propDesignCoolLossRate = 0 } = {} } = {},
    } = propResults;

    const {
        Annual: { HeatLoss: { _attributes: { total: refGrossHeatLoss } = {} } = {} } = {},
        Other: { _attributes: { designCoolLossRate: refDesignCoolLossRate = 0 } = {} } = {},
    } = refResults;

    const { aecNbc936: proposedAec = 0 } = getNetTotalEnergyConsumption(propResults);
    const { aecNbc936: refAec = 0 } = getNetTotalEnergyConsumption(refResults);

    const overallPercentDiff = (100 * (refAec - proposedAec)) / refAec;
    const heatLossPercentDiff = (100 * (refGrossHeatLoss - propGrossHeatLoss)) / refGrossHeatLoss;

    const { overall, heatLoss } = tierThresholds(totalHouseVolume);

    let proposedOverallTier = 0;
    if (overallPercentDiff >= 0) {
        Object.keys(overall).forEach((tier) => {
            if (overallPercentDiff >= overall[tier]) {
                proposedOverallTier = parseInt(tier);
            }
        });
    }

    let proposedHeatLossTier = 0;
    if (heatLossPercentDiff >= 0) {
        Object.keys(heatLoss).forEach((tier) => {
            if (heatLossPercentDiff >= heatLoss[tier]) {
                proposedHeatLossTier = parseInt(tier);
            }
        });
    }

    const coolingValidationPassed = refDesignCoolLossRate > propDesignCoolLossRate;

    return {
        proposedOverallTier,
        proposedHeatLossTier,
        calculatedTier: Math.min(proposedOverallTier, proposedHeatLossTier),
        coolingValidationPassed,
    };
};

export const getEuiComplianceTier = (modelData, propResults) => {
    //Calculate the EUI Compliance Tier in accordance with PCF 1869

    const {
        dimensions: {
            volume: { total: totalVolume = 0 } = {},
            belowGradeIntFloorArea: { total: bgFloorArea = 0 } = {},
            aboveGradeIntFloorArea: { total: agFloorArea = 0 } = {},
        } = {},
        specifications: { buildingSubType: { id: houseTypeId } = {} } = {},
        location: { weather: { location: { value: hddValue = 0 } = {} } = {} } = {},
    } = modelData;

    const {
        Annual: { HeatLoss: { _attributes: { total: proposedGrossHeatLoss } = {} } = {} } = {},
        f280Results: { latHeatGain, coolingOverrideSetPoint } = {},
    } = propResults;

    const { aecNbc936: proposedAec = 0 } = getNetTotalEnergyConsumption(propResults);

    const proposedGrossHeatLossKWh = proposedGrossHeatLoss * (1 / 0.0036);
    const proposedEnergyKWh = proposedAec * (1 / 0.0036);

    const totalHeatedFloorArea = bgFloorArea + agFloorArea;
    const coercedHeatedFloorArea = Math.max(115, Math.min(350, totalHeatedFloorArea));

    //Determine gross heat loss compliance
    const localHeatLossFactor = 0.02 * hddValue + 32.6;
    const grossSpaceHeatLossBudget = localHeatLossFactor * coercedHeatedFloorArea; //in kWh/y
    const grossHeatLossTierTargets = euiGrossHeatLossTargetTier(grossSpaceHeatLossBudget);

    const grossHeatLossTier = determineEuiCompliance(proposedGrossHeatLossKWh, grossHeatLossTierTargets);

    //Determine energy consumption compliance
    const localEnergyFactor = 0.02 * hddValue - 12.3;

    const energyUseBudget = localEnergyFactor * coercedHeatedFloorArea + 6500;
    const energyConsumptionTierTargets = euiEnergyConsumptionTargetTier(energyUseBudget);

    const energyTier = determineEuiCompliance(proposedEnergyKWh, energyConsumptionTierTargets);

    return Math.min(grossHeatLossTier, energyTier);
};

export const getClimateZone = (heatingDegreeDay) => {
    if (heatingDegreeDay < 3000) {
        return "4";
    } else if (heatingDegreeDay < 4000) {
        return "5";
    } else if (heatingDegreeDay < 5000) {
        return "6";
    } else if (heatingDegreeDay < 6000) {
        return "7a";
    } else if (heatingDegreeDay < 7000) {
        return "7b";
    } else {
        return "8";
    }
};

const bcStepCodeMeuiTargets = {
    climateZone4: {
        //climate zone
        3: {
            //step code level
            withCooling: {
                //with cooling
                50: 155, //{floorArea : maxMeui}
                75: 128, //{floorArea : maxMeui}
                120: 93, //{floorArea : maxMeui}
                165: 73, //{floorArea : maxMeui}
                210: 60, //{floorArea : maxMeui}
                100000: 55, //{floorArea : maxMeui}
            },
            noCooling: {
                //no cooling
                50: 120,
                75: 100,
                120: 75,
                165: 63,
                210: 53,
                100000: 50,
            },
        },
        4: {
            withCooling: {
                50: 125,
                75: 108,
                120: 78,
                165: 58,
                210: 48,
                100000: 45,
            },
            noCooling: {
                50: 90,
                75: 80,
                120: 60,
                165: 48,
                210: 40,
                100000: 40,
            },
        },
        5: {
            withCooling: {
                50: 100,
                75: 83,
                120: 58,
                165: 40,
                210: 33,
                100000: 30,
            },
            noCooling: {
                50: 65,
                75: 55,
                120: 40,
                165: 30,
                210: 25,
                100000: 25,
            },
        },
    },
    climateZone5: {
        3: {
            withCooling: {
                50: 170,
                75: 143,
                120: 108,
                165: 88,
                210: 75,
                100000: 70,
            },
            noCooling: {
                50: 135,
                75: 115,
                120: 90,
                165: 78,
                210: 68,
                100000: 65,
            },
        },
        4: {
            withCooling: {
                50: 135,
                75: 118,
                120: 88,
                165: 68,
                210: 58,
                100000: 55,
            },
            noCooling: {
                50: 100,
                75: 90,
                120: 70,
                165: 58,
                210: 50,
                100000: 50,
            },
        },
        5: {
            withCooling: {
                50: 105,
                75: 88,
                120: 63,
                165: 45,
                210: 38,
                100000: 35,
            },
            noCooling: {
                50: 70,
                75: 60,
                120: 45,
                165: 35,
                210: 30,
                100000: 30,
            },
        },
    },
    climateZone6: {
        3: {
            withCooling: {
                50: 180,
                75: 153,
                120: 118,
                165: 98,
                210: 85,
                100000: 80,
            },
            noCooling: {
                50: 145,
                75: 125,
                120: 100,
                165: 88,
                210: 78,
                100000: 75,
            },
        },
        4: {
            withCooling: {
                50: 140,
                75: 123,
                120: 93,
                165: 73,
                210: 63,
                100000: 60,
            },
            noCooling: {
                50: 105,
                75: 95,
                120: 75,
                165: 63,
                210: 55,
                100000: 55,
            },
        },
        5: {
            withCooling: {
                50: 115,
                75: 98,
                120: 73,
                165: 55,
                210: 48,
                100000: 45,
            },
            noCooling: {
                50: 80,
                75: 70,
                120: 55,
                165: 45,
                210: 40,
                100000: 40,
            },
        },
    },
    climateZone7a: {
        3: {
            withCooling: {
                50: 200,
                75: 173,
                120: 138,
                165: 118,
                210: 105,
                100000: 100,
            },
            noCooling: {
                50: 165,
                75: 145,
                120: 120,
                165: 108,
                210: 98,
                100000: 95,
            },
        },
        4: {
            withCooling: {
                50: 155,
                75: 138,
                120: 108,
                165: 88,
                210: 78,
                100000: 75,
            },
            noCooling: {
                50: 120,
                75: 110,
                120: 90,
                165: 78,
                210: 70,
                100000: 70,
            },
        },
        5: {
            withCooling: {
                50: 130,
                75: 113,
                120: 88,
                165: 70,
                210: 63,
                100000: 60,
            },
            noCooling: {
                50: 95,
                75: 85,
                120: 70,
                165: 60,
                210: 55,
                100000: 55,
            },
        },
    },
    climateZone7b: {
        3: {
            withCooling: {
                50: 220,
                75: 193,
                120: 158,
                165: 138,
                210: 125,
                100000: 120,
            },
            noCooling: {
                50: 185,
                75: 165,
                120: 140,
                165: 128,
                210: 118,
                100000: 115,
            },
        },
        4: {
            withCooling: {
                50: 170,
                75: 153,
                120: 123,
                165: 103,
                210: 93,
                100000: 90,
            },
            noCooling: {
                50: 135,
                75: 125,
                120: 105,
                165: 93,
                210: 85,
                100000: 85,
            },
        },
        5: {
            withCooling: {
                50: 140,
                75: 123,
                120: 98,
                165: 80,
                210: 73,
                100000: 70,
            },
            noCooling: {
                50: 105,
                75: 95,
                120: 80,
                165: 70,
                210: 65,
                100000: 65,
            },
        },
    },
    climateZone8: {
        3: {
            withCooling: {
                50: 235,
                75: 208,
                120: 173,
                165: 153,
                210: 140,
                100000: 135,
            },
            noCooling: {
                50: 200,
                75: 180,
                120: 155,
                165: 143,
                210: 133,
                100000: 130,
            },
        },
        4: {
            withCooling: {
                50: 185,
                75: 168,
                120: 138,
                165: 118,
                210: 108,
                100000: 105,
            },
            noCooling: {
                50: 150,
                75: 140,
                120: 120,
                165: 108,
                210: 100,
                100000: 100,
            },
        },
        5: {
            withCooling: {
                50: 150,
                75: 133,
                120: 108,
                165: 90,
                210: 83,
                100000: 80,
            },
            noCooling: {
                50: 115,
                75: 105,
                120: 90,
                165: 80,
                210: 75,
                100000: 75,
            },
        },
    },
};

const getBcStepCodeFromMeuiTargets = (meuiAchieved, climateZone, floorArea, coolingFlag = "withCooling") => {
    const meuiTargets = bcStepCodeMeuiTargets[climateZone];

    let floorAreaKey = 100000;
    if (floorArea <= 50) {
        floorAreaKey = 50;
    } else if (floorArea <= 75) {
        floorAreaKey = 75;
    } else if (floorArea <= 120) {
        floorAreaKey = 120;
    } else if (floorArea <= 165) {
        floorAreaKey = 165;
    } else if (floorArea <= 210) {
        floorAreaKey = 210;
    }

    if (meuiAchieved <= meuiTargets[5][coolingFlag][floorAreaKey]) {
        return 5;
    } else if (meuiAchieved <= meuiTargets[4][coolingFlag][floorAreaKey]) {
        return 4;
    } else if (meuiAchieved <= meuiTargets[3][coolingFlag][floorAreaKey]) {
        return 3;
    }
    return 0;
};

const bcStepCodeTediTargets = {
    climateZone4: {
        3: 30, //{step: tediMax}
        4: 20, //{step: tediMax}
        5: 15, //{step: tediMax}
    },
    climateZone5: {
        3: 40, //{step: tediMax}
        4: 30, //{step: tediMax}
        5: 20, //{step: tediMax}
    },
    climateZone6: {
        3: 50, //{step: tediMax}
        4: 40, //{step: tediMax}
        5: 25, //{step: tediMax}
    },
    climateZone7a: {
        3: 70, //{step: tediMax}
        4: 55, //{step: tediMax}
        5: 35, //{step: tediMax}
    },
    climateZone7b: {
        3: 90, //{step: tediMax}
        4: 65, //{step: tediMax}
        5: 50, //{step: tediMax}
    },
    climateZone8: {
        3: 105, //{step: tediMax}
        4: 80, //{step: tediMax}
        5: 60, //{step: tediMax}
    },
};

const climateZoneOrder = ["4", "5", "6", "7a", "7b", "8"];

const getBcStepCodeFromTediTargets = (tediAchieved, climateZone) => {
    const tediTargets = bcStepCodeTediTargets[climateZone];

    if (tediAchieved <= tediTargets[5]) {
        return 5;
    } else if (tediAchieved <= tediTargets[4]) {
        return 4;
    } else if (tediAchieved <= tediTargets[3]) {
        return 3;
    }
    return 0;
};

const roundDownToNearestThousand = (value) => {
    return Math.floor(value / 1000) * 1000;
};

const calculateAdjustedTedi = (hddValue) => {
    const climateZone = getClimateZone(hddValue);
    const climateZoneIndex = Math.max(0, Math.min(climateZoneOrder.indexOf(climateZone), climateZoneOrder.length - 1));
    const climateZoneHigher = climateZoneOrder[climateZoneIndex + 1];
    const climateZoneLower = climateZoneOrder[climateZoneIndex - 1];

    const adjustedTediTargets = {
        3: 0,
        4: 0,
        5: 0,
    };

    if (climateZone === "N/A") {
        return adjustedTediTargets;
    }

    [3, 4, 5].forEach((step) => {
        let tediAdjusted = 0;

        const tediStep = bcStepCodeTediTargets[`climateZone${climateZone}`][step]; //applicable maximum thermal energy demand intensity requirement in Tables 9.36.6.3.-A to 9.36.6.3.-F

        const hddActual = hddValue;

        if (hddValue < 3000) {
            // TEDIadjusted = TEDIstep + (TEDIhigher - TEDIstep)(HDDactual - HDDlowest)/500
            const tediHigher = bcStepCodeTediTargets[`climateZone${climateZoneHigher}`][step]; //maximum thermal energy demand intensity requirement for the same Step as stated in the subsequent Table of Tables 9.36.6.3.-A to 9.36.6.3.-F
            const hddLowest = 2500;
            tediAdjusted = tediStep + ((tediHigher - tediStep) * (hddActual - hddLowest)) / 500;
        } else if (hddValue < 6999) {
            // TEDIadjusted = TEDIstep + (TEDIhigher - TEDIstep)(HDDactual - HDDlowest)/1000
            const tediHigher = bcStepCodeTediTargets[`climateZone${climateZoneHigher}`][step]; //maximum thermal energy demand intensity requirement for the same Step as stated in the subsequent Table of Tables 9.36.6.3.-A to 9.36.6.3.-F
            const hddLowest = roundDownToNearestThousand(hddValue);

            tediAdjusted = tediStep + ((tediHigher - tediStep) * (hddActual - hddLowest)) / 1000;
        } else {
            // TEDIadjusted = TEDIstep + (TEDIstep - TEDIlower)(HDDactual - HDDlowest)/1000
            const tediLower = bcStepCodeTediTargets[`climateZone${climateZoneLower}`][step]; // maximum thermal energy demand intensity requirement for the same Step as stated in the preceding Table of Tables 9.36.6.3.-A to 9.36.6.3.-F
            const hddLowest = 7000;
            tediAdjusted = tediStep + ((tediStep - tediLower) * (hddActual - hddLowest)) / 1000;
        }

        adjustedTediTargets[step] = Math.round(tediAdjusted * 100) / 100;
    });

    return adjustedTediTargets;
};

const bcStepCodeAirTightnessTargets = {
    3: 2.5, //{step: airTightnessMax}
    4: 1.5,
    5: 1.0,
};

const getBcStepCodeFromAirTightness = (airTightness) => {
    if (airTightness <= 1) {
        return 5;
    } else if (airTightness <= 1.5) {
        return 4;
    } else if (airTightness <= 2.5) {
        return 3;
    }
    return 0;
};

export const getBCStepComplianceValues = (
    modelData,
    upgradePackage = {},
    resultsData = {},
    propResultsKey,
    refResultsKey = "ersReference"
) => {
    //Calculate the BC Step Compliance Tier in accordance with BC Step Code (2023 revision)

    const propResults = resultsData?.[propResultsKey] || {};
    const refResults = resultsData?.[refResultsKey] || {};

    const { Annual: { HeatLoss: { _attributes: { total: proposedGrossHeatLoss } = {} } = {} } = {} } = propResults;
    const { Annual: { HeatLoss: { _attributes: { total: referenceGrossHeatLoss } = {} } = {} } = {} } = refResults;

    let {
        dimensions: {
            volume: { total: totalHouseVolume = 0 } = {},
            belowGradeIntFloorArea: { total: bgFloorArea = 0 } = {},
            aboveGradeIntFloorArea: { total: agFloorArea = 0 } = {},
        } = {},
        location: { weather: { location: { value: hddValue = 0 } = {} } = {} } = {},
        airTightness: { blowerTest: { airChangeRate50Pa } = {} } = {},
        heatingCooling: { cooling: { system: coolingSystem = {} } = {} } = {},
    } = modelData;

    if (!isEmpty(upgradePackage)) {
        const { upgrades = {} } = upgradePackage;

        const airTightnessUpgradeMatch = Object.values(upgrades).find((el) => el.upgradeType === "airTightness") || {};
        const heatingCoolingUpgradeMatch =
            Object.values(upgrades).find((el) => el.upgradeType === "heatingCooling") || {};

        if (!isEmpty(airTightnessUpgradeMatch)) {
            const { fields: { blowerTest: { airChangeRate50Pa: upgradedAirChangeRate50Pa } = {} } = {} } =
                airTightnessUpgradeMatch;
            airChangeRate50Pa = upgradedAirChangeRate50Pa;
        }

        if (!isEmpty(heatingCoolingUpgradeMatch)) {
            const { fields: { cooling: { system: upgradedCoolingSystem = {} } = {} } = {} } =
                heatingCoolingUpgradeMatch;
            coolingSystem = upgradedCoolingSystem;
        }
    }

    const coolingType = Object.keys(coolingSystem || {})[0];
    const coolingFlag = ["airConditioning", "airHeatPump", "waterHeatPump", "groundHeatPump"].includes(coolingType)
        ? "withCooling"
        : "noCooling";

    const climateZone = getClimateZone(hddValue);

    if (climateZone === "N/A") {
        return 0;
    }

    const { aecNbc936: proposedAec = 0 } = getNetTotalEnergyConsumption(propResults);
    const { aecNbc936: refAec = 0 } = getNetTotalEnergyConsumption(refResults);

    const { Annual: { Load: { _attributes: { auxiliaryEnergy = 0 } = {} } = {} } = {} } = propResults;

    const totalHeatedFloorArea = bgFloorArea + agFloorArea;

    const tediAchieved = (auxiliaryEnergy * (1 / 3.6)) / totalHeatedFloorArea;
    const meuiAchieved = (proposedAec * (1 / 0.0036)) / totalHeatedFloorArea; //proposed AEC without baseloads

    const airTightnessStep = getBcStepCodeFromAirTightness(airChangeRate50Pa);
    const tediStep = getBcStepCodeFromTediTargets(tediAchieved, `climateZone${climateZone}`);
    const meuiStep = getBcStepCodeFromMeuiTargets(
        meuiAchieved,
        `climateZone${climateZone}`,
        totalHeatedFloorArea,
        coolingFlag
    );

    //Calculate alternative metrics (aligned with NBC 2020)
    let grossHeatLossReduction = null;
    let overallPercentReduction = null;
    if (!isEmpty(refResults)) {
        grossHeatLossReduction = (100 * (referenceGrossHeatLoss - proposedGrossHeatLoss)) / referenceGrossHeatLoss;
        overallPercentReduction = (100 * (refAec - proposedAec)) / refAec;
    }

    const adjustedTediTargets = calculateAdjustedTedi(hddValue);
    let adjustedTediStep = 0;
    let adjustedTediThreshold = 0;
    Object.keys(adjustedTediTargets).forEach((step) => {
        if (tediAchieved <= adjustedTediTargets[step]) {
            adjustedTediStep = parseInt(step);
            adjustedTediThreshold = adjustedTediTargets[step];
        }
    });

    const overallStepAchieved = Math.min(airTightnessStep, Math.max(tediStep, adjustedTediStep), meuiStep);

    const { calculatedTier } = getNbc2020Tier(propResults, refResults, totalHouseVolume);

    let bcStepFromNbcTier = 0;
    if (calculatedTier === 3 && airTightnessStep >= 3) {
        bcStepFromNbcTier = 3;
    } else if (calculatedTier === 4 && airTightnessStep >= 4) {
        bcStepFromNbcTier = 4;
    } else if (calculatedTier === 5 && airTightnessStep >= 5) {
        bcStepFromNbcTier = 5;
    }

    return {
        overallStepAchieved,
        bcStepFromNbcTier,
        airTightnessStep,
        tediStep,
        adjustedTediStep,
        adjustedTediThreshold,
        meuiStep,
        grossHeatLossReduction,
        overallPercentReduction,
    };
};
